import { supabase } from "@/integrations/supabase/client";
import { hasAnalyticsConsentNow } from "@/lib/cookieConsent";
import { detectDevice, detectInAppBrowser, readSource } from "@/lib/quickApplyTracking";

/**
 * Enige schrijver van onboarding-meting (ob_* events + bron bij account).
 * - Fire-and-forget: nooit awaited, fouten stil genegeerd, de flow wacht nergens op.
 * - Nooit naam, e-mail, telefoon of vrije tekst. De database-functie houdt
 *   dezelfde allowlist aan en haalt user_id zelf uit de sessie.
 */
export type OnboardingEvent =
  | "ob_signup_opened"
  | "ob_signup_error"
  | "ob_job_type_selected"
  | "ob_enrich_cv"
  | "ob_enrich_manual"
  | "ob_phase2_completed";

export type SignupErrorCode = "email_exists" | "weak_password" | "pwned" | "too_short" | "invalid_email" | "other";

const FIRST_TOUCH_KEY = "cw_first_touch";
const SESSION_KEY = "cw_ob_session";
const SOURCE_DONE_KEY = "cw_signup_source_done";
const ALLOWED = new Set(["code", "entry", "device", "in_app", "consent", "source", "track"]);
const sent = new Set<string>();

export function classifySignupError(code: string | null | undefined, reason?: string | null, pwLength?: number): SignupErrorCode {
  if (typeof pwLength === "number" && pwLength > 0 && pwLength < 8) return "too_short";
  if (code === "email_exists") return "email_exists";
  if (code === "weak_password") return reason === "pwned" ? "pwned" : "weak_password";
  if (code === "invalid_email") return "invalid_email";
  return "other";
}

export function entryFromPath(path: string): "start" | "start-2" | "start-3" | "signup" {
  if (path === "/start-2" || path.startsWith("/start-2/")) return "start-2";
  if (path === "/start-3" || path.startsWith("/start-3/")) return "start-3";
  if (path === "/start" || path === "/start/") return "start";
  return "signup";
}

export function filterData(input: Record<string, unknown>): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const [k, v] of Object.entries(input)) {
    if (!ALLOWED.has(k) || v === undefined || v === null) continue;
    out[k] = typeof v === "boolean" ? v : String(v).slice(0, 40);
  }
  return out;
}

type FirstTouch = { entry: string; source: string; utm_source: string | null; utm_campaign: string | null; utm_content: string | null; boost_id: string | null };

function safeSession(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** Eerste pagina van deze tab onthouden (alleen sessionStorage, geen cookie). */
export function captureFirstTouch(): void {
  const ss = safeSession();
  if (!ss || ss.getItem(FIRST_TOUCH_KEY)) return;
  try {
    const src = readSource(window.location.search);
    const utm_content = new URLSearchParams(window.location.search).get("utm_content");
    const ft: FirstTouch = { entry: entryFromPath(window.location.pathname), ...src, utm_content: utm_content ? utm_content.slice(0, 80) : null };
    ss.setItem(FIRST_TOUCH_KEY, JSON.stringify(ft));
  } catch { /* nooit blokkeren */ }
}

function firstTouch(): FirstTouch | null {
  try { const raw = safeSession()?.getItem(FIRST_TOUCH_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

function obSession(): string {
  const ss = safeSession();
  let id = ss?.getItem(SESSION_KEY) ?? null;
  if (!id) {
    try { id = crypto.randomUUID(); } catch { id = `ob_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`; }
    try { ss?.setItem(SESSION_KEY, id); } catch { /* geheugen is genoeg */ }
  }
  return id;
}

function context() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return { device: detectDevice(ua, window.innerWidth), in_app: detectInAppBrowser(ua), consent: hasAnalyticsConsentNow() };
}

export function trackOnboarding(event: OnboardingEvent, extra: { code?: string; track?: string } = {}): void {
  try {
    const key = event === "ob_signup_error" ? `${event}:${Date.now()}` : event;
    if (sent.has(key)) return;
    sent.add(key);
    const ft = firstTouch();
    const data = filterData({ ...context(), entry: ft?.entry ?? entryFromPath(window.location.pathname), source: ft?.source, ...extra });
    void supabase.rpc("track_onboarding_event", { p_event: event, p_session: obSession(), p_data: data as never })
      .then(() => undefined, () => undefined);
  } catch { /* meting mag de flow nooit hinderen */ }
}

/** Bron één keer koppelen aan een nieuw kandidaat-account (server negeert oudere accounts). */
export function recordSignupSource(): void {
  try {
    const ss = safeSession();
    if (ss?.getItem(SOURCE_DONE_KEY)) return;
    ss?.setItem(SOURCE_DONE_KEY, "1");
    const ft = firstTouch();
    const p = { ...(ft ?? { entry: "signup", source: "direct" }), ...context() };
    void supabase.rpc("record_signup_source", { p: p as never }).then(() => undefined, () => undefined);
  } catch { /* stil */ }
}
